import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import type {Finding} from './types'

/** Display order for severity levels (most severe first) */
export const SEVERITY_ORDER: RuleSeverity[] = [
  'failure',
  'critical',
  'high',
  'error',
  'medium',
  'warning',
  'low',
  'note',
  'nit',
  'notice',
  'none',
]

const getSeveritySortIndex = (severity: RuleSeverity | undefined): number => {
  if (!severity) {
    return SEVERITY_ORDER.length
  }

  const index = SEVERITY_ORDER.indexOf(severity)
  return index === -1 ? SEVERITY_ORDER.length : index
}

const SEVERITY_MAP: Record<string, RuleSeverity> = {
  critical: 'critical',
  high: 'high',
  moderate: 'medium',
  low: 'low',
}

const SOURCE_LABELS: Record<string, string> = {
  check_suite: 'Actions',
  pull_request_backfill: 'Actions',
  check_run: 'Actions',
  pr_review_comment: 'Review comment',
  ccr_review_comment: 'Copilot',
  ci_analysis: 'CI Analysis',
}

const DISPLAY_PATH_SEGMENTS = 3

export const getFindingSeverity = (finding: Finding): RuleSeverity | undefined => {
  return SEVERITY_MAP[finding.severity]
}

export const getFindingSource = (finding: Finding): string => {
  return SOURCE_LABELS[finding.source] ?? finding.source
}

export interface SourceLink {
  label: string
  url: string
}

export const getFindingSourceLinks = (finding: Finding): SourceLink[] => {
  const links: SourceLink[] = []
  if (!finding.sourceUrls) return links

  for (const [sourceType, urls] of Object.entries(finding.sourceUrls)) {
    const url = urls[0]
    if (!url) continue
    const label = SOURCE_LABELS[sourceType] ?? sourceType
    links.push({label, url})
  }
  return links
}

export const getDisplayFilePath = (filePath: string): string => {
  const segments = filePath.replace(/\\/g, '/').split('/').filter(Boolean)
  if (segments.length <= DISPLAY_PATH_SEGMENTS) return filePath

  return `.../${segments.slice(-DISPLAY_PATH_SEGMENTS).join('/')}`
}

const getFindingLocationSuffix = (finding: Finding): string => {
  if (finding.startLine === finding.endLine) {
    let suffix = `:${finding.startLine}`
    if (finding.startColumn > 0) {
      suffix += `:${finding.startColumn}`
    }
    return suffix
  }

  return `:${finding.startLine}-${finding.endLine}`
}

export const getFindingFullFileLocation = (finding: Finding): string | null => {
  if (!finding.filePath) return null

  return `${finding.filePath}${getFindingLocationSuffix(finding)}`
}

export const getFindingFileLocation = (finding: Finding): string | null => {
  if (!finding.filePath) return null

  return `${getDisplayFilePath(finding.filePath)}${getFindingLocationSuffix(finding)}`
}

export const getFindingTitle = (finding: Finding): string => {
  const msg = finding.groupTitle
  if (msg.length <= 100) return msg
  const truncated = msg.slice(0, 100)
  const lastSpace = truncated.lastIndexOf(' ')
  return lastSpace > 0 ? `${truncated.slice(0, lastSpace)}…` : `${truncated}…`
}

export const sortFindingsBySeverity = (findings: Finding[]): Finding[] => {
  return [...findings].sort((a, b) => {
    const indexA = getSeveritySortIndex(getFindingSeverity(a))
    const indexB = getSeveritySortIndex(getFindingSeverity(b))
    return indexA - indexB
  })
}

const HIGH_ATTENTION_SEVERITIES: ReadonlySet<RuleSeverity> = new Set(['critical', 'high'])

export const isNeedsAttention = (finding: Finding): boolean => {
  const severity = getFindingSeverity(finding)
  return !!severity && HIGH_ATTENTION_SEVERITIES.has(severity)
}
